import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    Users,
    UserCheck,
    UserX,
    UserPlus,
    Activity,
    Link as LinkIcon,
    CheckCircle,
    XCircle,
    Clock,
    BookOpen,
    Layers,
    RefreshCw,
    Mail,
    Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
    const { language } = useLanguage();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchStats();
    }, []);

    const getAuthConfig = () => {
        const token = localStorage.getItem('nusatrade_token');
        return { headers: { 'Authorization': `Bearer ${token}` } };
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/admin/stats`, getAuthConfig());
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchStats();
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'short'
        });
    };

    // Calculate max for chart scaling
    const maxDailyActive = stats?.daily_active_users 
        ? Math.max(...stats.daily_active_users.map(d => d.count), 1) 
        : 1;

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-6">
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <Skeleton key={i} className="h-20" />
                        ))}
                    </div>
                    <Skeleton className="h-64" />
                </div>
            </AdminLayout>
        );
    }

    const topStats = [
        { key: 'total_users', label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
        { key: 'active_users', label: 'Active', value: stats?.active_users || 0, icon: UserCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
        { key: 'blocked_users', label: 'Blocked', value: stats?.blocked_users || 0, icon: UserX, color: 'text-red-400', bg: 'bg-red-500/10' },
        { key: 'new_users_7d', label: 'New (7d)', value: stats?.new_users_7d || 0, icon: UserPlus, color: 'text-purple-400', bg: 'bg-purple-500/10' },
        { key: 'active_today', label: 'Active Today', value: stats?.active_today || 0, icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/10' },
        { key: 'total_invitations', label: 'Invitations', value: stats?.total_invitations || 0, icon: LinkIcon, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
        { key: 'total_levels', label: 'Levels', value: stats?.total_levels || 0, icon: Layers, color: 'text-pink-400', bg: 'bg-pink-500/10' },
        { key: 'total_lessons', label: 'Lessons', value: stats?.total_lessons || 0, icon: BookOpen, color: 'text-indigo-400', bg: 'bg-indigo-500/10' }
    ];

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-heading font-bold text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        Overview
                    </h1>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="text-slate-400 hover:text-white"
                    >
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>

                {/* Top Stats Row */}
                <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                    {topStats.map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={stat.key}
                                className={`${stat.bg} rounded-lg p-3 border border-slate-700/50`}
                                data-testid={`stat-${stat.key}`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Icon className={`w-4 h-4 ${stat.color}`} />
                                    <span className="text-xs text-slate-400 truncate">{stat.label}</span>
                                </div>
                                <p className={`text-2xl font-bold ${stat.color}`}>
                                    {stat.value}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Data Sources Info */}
                <div className="bg-slate-800/50 rounded-lg px-4 py-2 border border-slate-700/50">
                    <p className="text-sm text-slate-400 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-primary" />
                        Data Sources: {stats?.total_users || 0} users + {stats?.total_invitations || 0} invitations = <span className="text-white font-medium">{(stats?.total_users || 0) + (stats?.total_invitations || 0)} total records</span>
                    </p>
                </div>

                {/* Middle Section - 3 Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Invitation Stats */}
                    <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                                <LinkIcon className="w-4 h-4" />
                                Invitation Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400 text-sm flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    Active
                                </span>
                                <span className="text-emerald-400 font-bold">{stats?.active_invitations || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400 text-sm flex items-center gap-2">
                                    <Users className="w-4 h-4 text-blue-400" />
                                    Used
                                </span>
                                <span className="text-blue-400 font-bold">{stats?.used_invitations || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400 text-sm flex items-center gap-2">
                                    <XCircle className="w-4 h-4 text-red-400" />
                                    Expired
                                </span>
                                <span className="text-red-400 font-bold">{stats?.expired_invitations || 0}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recent Registrations */}
                    <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                                <UserPlus className="w-4 h-4" />
                                Recent Registrations
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats?.recent_users?.length > 0 ? (
                                <div className="space-y-2">
                                    {stats.recent_users.slice(0, 4).map((user, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2 truncate">
                                                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-medium">
                                                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                                </div>
                                                <span className="text-white truncate max-w-[100px]">{user.name}</span>
                                            </div>
                                            <span className="text-slate-500 text-xs">{formatDate(user.created_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500 text-sm text-center py-4">No recent registrations</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Content Overview */}
                    <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                                <BookOpen className="w-4 h-4" />
                                Content Overview
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-white">{stats?.total_levels || 0}</p>
                                    <p className="text-xs text-slate-400">Levels</p>
                                </div>
                                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-white">{stats?.total_lessons || 0}</p>
                                    <p className="text-xs text-slate-400">Lessons</p>
                                </div>
                                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-white">{stats?.total_resources || 0}</p>
                                    <p className="text-xs text-slate-400">Resources</p>
                                </div>
                                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-white">{stats?.total_insights || 0}</p>
                                    <p className="text-xs text-slate-400">Insights</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Daily Active Users Chart */}
                <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                            <Activity className="w-4 h-4 text-primary" />
                            Daily Active Users (Last 30 Days)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-48 flex items-end gap-1">
                            {stats?.daily_active_users?.map((day, idx) => {
                                const height = day.count > 0 
                                    ? Math.max((day.count / maxDailyActive) * 100, 10) 
                                    : 5;
                                return (
                                    <div
                                        key={idx}
                                        className="flex-1 group relative"
                                        title={`${day.date}: ${day.count} users`}
                                    >
                                        <div
                                            className="w-full bg-primary hover:bg-primary/80 rounded-t transition-all cursor-pointer"
                                            style={{ height: `${height}%` }}
                                        />
                                        {/* Tooltip */}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-700 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                            {day.date}: {day.count}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                            <span>30 days ago</span>
                            <span>Today</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};
